import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
  CircularProgress,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Warning } from '@bb-ui/icons/dist/small/Warning';

interface ExecuteCohortConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cohortName: string;
  downtimeDate: string | null;
  sitesCount: number;
  loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '500px',
    maxWidth: '600px',
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    color: theme.palette.warning.main,
  },
  warningIcon: {
    fontSize: '24px',
  },
  contentSection: {
    marginBottom: theme.spacing(3),
  },
  highlightedText: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  cohortInfo: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  actionsList: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5),
  },
  confirmButton: {
    minWidth: '180px',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}));

const ExecuteCohortConfirmDialog: React.FC<ExecuteCohortConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  cohortName,
  downtimeDate,
  sitesCount,
  loading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const formattedDate = downtimeDate
    ? new Date(downtimeDate).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : '';

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={!loading ? onClose : undefined} closeLabel={t('global.close')}>
        {t('cohorts.executeCohortDialog.title')}
      </DialogTitle>

      <DialogContent>
        <div className={classes.warningHeader}>
          <Warning className={classes.warningIcon} />
          <Typography variant="h6">{t('cohorts.executeCohortDialog.warningHeader')}</Typography>
        </div>

        <div className={classes.contentSection}>
          <Typography variant="body1" gutterBottom>
            {t('cohorts.executeCohortDialog.description')}
          </Typography>

          <ul className={classes.actionsList}>
            <li>
              <Typography variant="body2">{t('cohorts.executeCohortDialog.action1')}</Typography>
            </li>
            <li>
              <Typography variant="body2">{t('cohorts.executeCohortDialog.action2')}</Typography>
            </li>
            <li>
              <Typography variant="body2">{t('cohorts.executeCohortDialog.action3')}</Typography>
            </li>
          </ul>
        </div>

        <div className={classes.cohortInfo}>
          <Typography variant="body2">
            <strong>{t('cohorts.cohort')}:</strong> {cohortName}
          </Typography>
          <Typography variant="body2">
            <strong>{t('cohorts.downtimeDate')}:</strong> {formattedDate}
          </Typography>
          <Typography variant="body2">
            <strong>{t('cohorts.sitesCount')}:</strong> {sitesCount}
          </Typography>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={onClose} disabled={loading}>
          {t('global.cancel')}
        </DefaultButton>
        <PrimaryButton
          onClick={handleConfirm}
          color="primary"
          className={classes.confirmButton}
          disabled={loading}
        >
          <div className={classes.buttonContent}>
            {loading && <CircularProgress size={16} color="inherit" id="loading" label="" />}
            <span>
              {loading
                ? t('cohorts.executeCohortDialog.executing')
                : t('cohorts.executeCohortDialog.confirm')}
            </span>
          </div>
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExecuteCohortConfirmDialog;
